import type { z } from 'zod';
import { type EndpointDefinition, type JsonEndpoint, type StreamEndpoint } from '../contracts';
import { WireErrorSchema, type WireError } from '../errors/wire-error.schema';

/**
 * Tiny typed client walker. Given a `contracts` tree and a `fetcher` (any
 * function with `fetch`-compatible semantics), returns an object whose
 * shape mirrors the tree: `client.<branch>.<endpoint>(args)` calls the
 * matching wire endpoint and returns a discriminated `{ status, body }`.
 *
 * The 200 response is validated against the leaf's `response` schema. Other
 * statuses return the raw decoded body typed as `WireError` (the wire
 * envelope from ADR 0013).
 *
 * Why hand-rolled: ts-rest 3.x still depends on zod v3 + fastify v4 at the
 * type level and degrades to `any` in this monorepo. The contract tree
 * shape is the load-bearing decision, not the library — see README §why
 * hand-rolled for the trade-off.
 */

/**
 * Loose `fetch`-compatible signature. Types `body` and `credentials` as
 * strings instead of pulling from `lib.dom` so this package can build
 * without a DOM target (it's consumed by the backend too).
 */
export type FetcherInit = {
  readonly method: string;
  readonly headers: Record<string, string>;
  readonly body?: string;
};

export type FetcherResponse = {
  readonly status: number;
  text(): Promise<string>;
  /** Optional so a fetcher that cannot expose headers still satisfies the type. */
  header?(name: string): string | null;
};

export type Fetcher = (url: string, init: FetcherInit) => Promise<FetcherResponse>;

export type ClientOptions = {
  readonly baseUrl: string;
  readonly fetcher?: Fetcher;
  readonly baseHeaders?: Record<string, string>;
  /** This bundle's build. Omit when unknown — the comparison then never runs. */
  readonly release?: string;
  /** Called at most once per distinct server release. */
  readonly onReleaseMismatch?: (serverRelease: string) => void;
};

/** The release the API answered with. Omitted by a backend that does not know its own. */
export const RELEASE_HEADER = 'X-Immo-Release';

type EndpointIdentity = { readonly method: string; readonly path: string };

/**
 * A success-status body that does not match the contract, with no evidence that
 * the two sides are on different releases. This is the loud one: it means the
 * wire contract itself is broken, and it must stay as loud as it was before
 * `StaleClientError` existed.
 */
export class ContractMismatchError extends Error {
  constructor(
    readonly endpoint: EndpointIdentity,
    readonly issues: ReadonlyArray<z.core.$ZodIssue>,
  ) {
    super(`Response did not match contract for ${endpoint.method} ${endpoint.path}`);
    this.name = 'ContractMismatchError';
  }
}

/**
 * The same parse failure, but the server answered from a release this bundle was
 * not built against — so the tab is out of date and a reload fixes it. Decided
 * on that evidence alone, never on the shape of the issues.
 */
export class StaleClientError extends Error {
  readonly code = 'app.outdated' as const;

  constructor(
    readonly endpoint: EndpointIdentity,
    readonly clientRelease: string,
    readonly serverRelease: string,
    readonly issues: ReadonlyArray<z.core.$ZodIssue>,
  ) {
    super(`Client ${clientRelease} cannot read a ${serverRelease} response for ${endpoint.method} ${endpoint.path}`);
    this.name = 'StaleClientError';
  }
}

type EndpointArgs<E extends EndpointDefinition> = (E extends { query: z.ZodTypeAny }
  ? { query: z.input<E['query'] & {}> }
  : Record<never, never>) &
  (E extends { body: z.ZodTypeAny } ? { body: z.input<E['body'] & {}> } : Record<never, never>) &
  (E extends { pathParams: z.ZodTypeAny } ? { pathParams: z.input<E['pathParams'] & {}> } : Record<never, never>);

type SuccessStatus<E extends JsonEndpoint> = keyof E['response'] & number;
type SuccessBody<E extends JsonEndpoint> = {
  [K in SuccessStatus<E>]: E['response'][K] extends z.ZodTypeAny ? z.output<E['response'][K]> : never;
}[SuccessStatus<E>];

type EndpointSuccessResponse<E extends JsonEndpoint> = {
  readonly ok: true;
  readonly status: SuccessStatus<E>;
  readonly body: SuccessBody<E>;
};

type EndpointErrorResponse = {
  readonly ok: false;
  readonly status: number;
  readonly body: WireError;
};

export type EndpointResult<E extends JsonEndpoint> = EndpointSuccessResponse<E> | EndpointErrorResponse;

type JsonEndpointCaller<E extends JsonEndpoint> = keyof EndpointArgs<E> extends never
  ? () => Promise<EndpointResult<E>>
  : (args: EndpointArgs<E>) => Promise<EndpointResult<E>>;

/**
 * Stream endpoints have no JSON request/response cycle: the caller opens the
 * resolved URL with an `EventSource`. The client surface for a stream leaf is
 * therefore a synchronous URL builder, not an async fetch.
 */
type StreamEndpointCaller<E extends StreamEndpoint> = keyof EndpointArgs<E> extends never
  ? () => string
  : (args: EndpointArgs<E>) => string;

type EndpointCaller<E extends EndpointDefinition> = E extends StreamEndpoint
  ? StreamEndpointCaller<E>
  : E extends JsonEndpoint
    ? JsonEndpointCaller<E>
    : never;

export type TypedClient<C extends Record<string, Record<string, EndpointDefinition>>> = {
  [B in keyof C]: {
    [E in keyof C[B]]: C[B][E] extends EndpointDefinition ? EndpointCaller<C[B][E]> : never;
  };
};

export function createTypedClient<C extends Record<string, Record<string, EndpointDefinition>>>(
  contracts: C,
  options: ClientOptions,
): TypedClient<C> {
  const fetcher: Fetcher = options.fetcher ?? defaultFetcher;
  const baseUrl = trimTrailingSlash(options.baseUrl);
  const noteServerRelease = createReleaseWatcher(options);

  const out: Record<string, Record<string, (args?: Record<string, unknown>) => unknown>> = {};

  for (const [branchName, branch] of Object.entries(contracts)) {
    out[branchName] = {};

    for (const [endpointName, endpoint] of Object.entries(branch)) {
      if (endpoint.kind === 'stream') {
        // No JSON cycle: hand back the resolved URL for the caller to open with
        // an `EventSource`. Same `pathParams` / `query` interpolation as a fetch.
        const streamEndpoint = endpoint;
        out[branchName][endpointName] = (args: Record<string, unknown> = {}) =>
          buildUrl(
            baseUrl,
            streamEndpoint.path,
            args['pathParams'] as Record<string, string | number> | undefined,
            args['query'] as Record<string, unknown> | undefined,
          );
      } else {
        const jsonEndpoint = endpoint;
        out[branchName][endpointName] = (args: Record<string, unknown> = {}) =>
          callEndpoint(
            jsonEndpoint,
            args,
            baseUrl,
            fetcher,
            options.baseHeaders ?? {},
            noteServerRelease,
            options.release,
          );
      }
    }
  }

  return out as unknown as TypedClient<C>;
}

async function callEndpoint(
  endpoint: JsonEndpoint,
  args: Record<string, unknown>,
  baseUrl: string,
  fetcher: Fetcher,
  baseHeaders: Record<string, string>,
  noteServerRelease: ReleaseWatcher,
  clientRelease: string | undefined,
): Promise<EndpointResult<JsonEndpoint>> {
  const url = buildUrl(
    baseUrl,
    endpoint.path,
    args['pathParams'] as Record<string, string | number> | undefined,
    args['query'] as Record<string, unknown> | undefined,
  );

  const headers: Record<string, string> = { ...baseHeaders };
  const hasBody = endpoint.body !== undefined;

  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  const init: FetcherInit = {
    method: endpoint.method,
    headers,
    ...(hasBody ? { body: JSON.stringify(args['body'] ?? {}) } : {}),
  };

  const response = await fetcher(url, init);

  // Left unread when this bundle does not know its own release: nothing can
  // consume the server's then, and a fetcher is free to implement `header()`
  // however it likes — touching it for a value no branch will use is gratuitous.
  const serverRelease = clientRelease === undefined ? undefined : readServerRelease(response);

  // Before the parse branch, so a drift is observed on the very request whose
  // body is about to fail to match — and so the throw site below can read it.
  noteServerRelease(serverRelease);

  const rawBody = await readJsonSafely(response);

  const successSchema = endpoint.response[response.status];

  if (successSchema !== undefined) {
    const parsed = successSchema.safeParse(rawBody);

    if (parsed.success) {
      return { ok: true, status: response.status, body: parsed.data } as EndpointResult<JsonEndpoint>;
    }

    // Schema mismatch at the success branch is a wire-contract bug,
    // not a transport one. Throw so the caller's error boundary catches
    // it; the network round-trip succeeded but the contract is broken.
    //
    // Evidence, not heuristics: only a PROVEN release difference downgrades this
    // from "our contract is broken" to "your tab is old". An unknown release on
    // either side keeps the loud branch, because a contract bug rendered as
    // "reload me" tells the user to reload forever and hides the defect.
    if (clientRelease !== undefined && serverRelease !== undefined && clientRelease !== serverRelease) {
      throw new StaleClientError(endpoint, clientRelease, serverRelease, parsed.error.issues);
    }

    throw new ContractMismatchError(endpoint, parsed.error.issues);
  }

  const wireError = WireErrorSchema.safeParse(rawBody);

  return {
    ok: false,
    status: response.status,
    body: wireError.success ? wireError.data : { error: {} },
  };
}

type ReleaseWatcher = (serverRelease: string | undefined) => void;

/** An absent header and an empty one both mean "the server does not know its release". */
function readServerRelease(response: FetcherResponse): string | undefined {
  const value = response.header?.(RELEASE_HEADER) ?? null;

  return value === null || value.length === 0 ? undefined : value;
}

/**
 * Compares the release the server answered with against the one this bundle was
 * built as, and reports each distinct differing server release once.
 *
 * The comparison is inequality, never ordering: a rollback drifts exactly like a
 * roll-forward and wants the same reload, and dev builds carry `sha-…` tags no
 * version parser can rank. The already-reported releases are a SET rather than a
 * last-seen value because a rolling deploy answers alternately from old and new
 * instances, which a last-seen check would re-report on every flip.
 */
function createReleaseWatcher(options: ClientOptions): ReleaseWatcher {
  const clientRelease = options.release;
  const onReleaseMismatch = options.onReleaseMismatch;

  if (clientRelease === undefined || onReleaseMismatch === undefined) {
    return () => {};
  }

  const reported = new Set<string>();

  return (serverRelease) => {
    if (serverRelease === undefined) return;

    if (serverRelease === clientRelease) return;

    if (reported.has(serverRelease)) return;

    reported.add(serverRelease);
    onReleaseMismatch(serverRelease);
  };
}

function buildUrl(
  baseUrl: string,
  path: string,
  pathParams: Record<string, string | number> | undefined,
  query: Record<string, unknown> | undefined,
): string {
  let resolved = path;

  if (pathParams !== undefined) {
    for (const [key, value] of Object.entries(pathParams)) {
      resolved = resolved.replace(`:${key}`, encodeURIComponent(String(value)));
    }
  }

  if (query !== undefined && Object.keys(query).length > 0) {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    }

    const qs = params.toString();

    if (qs.length > 0) {
      resolved += `?${qs}`;
    }
  }

  return `${baseUrl}${resolved}`;
}

async function readJsonSafely(response: FetcherResponse): Promise<unknown> {
  const text = await response.text();

  if (text.length === 0) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function trimTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

function defaultFetcher(url: string, init: FetcherInit): Promise<FetcherResponse> {
  // `fetch` is available in modern runtimes (Node 18+, browsers, Workers).
  // Typed loosely so the package doesn't depend on `lib.dom`.
  return (globalThis as { fetch(u: string, i: unknown): Promise<FetcherResponse> }).fetch(url, init);
}
